import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PriorityMappingEditor } from "@/components/itsm-config/mapping-editors";
import { ScopeReveal } from "@/components/itsm-config/scope-reveal";
import { useGeneralMappingSave } from "@/hooks/use-general-mapping-save";
import { useExternalPriorityOptions } from "@/hooks/use-itsm-metadata";
import { findRule } from "@/lib/itsm-config/general-mapping";
import {
  HARMONY_PRIORITIES,
  HARMONY_PRIORITY_LABELS,
  PROVIDER_LABELS,
  type GeneralMappingRule,
  type ProviderConfig,
  type ItsmProviderFamily,
} from "@/lib/types/itsm-config";

interface Props {
  tenantId: string;
  credentialId: string;
  provider: string;
  family: ItsmProviderFamily;
  providerConfig: ProviderConfig;
}

/** Card 2 — Harmony priority → external priority (tenant-wide + exceptions). */
export function PriorityMappingSection({
  tenantId,
  credentialId,
  provider,
  family,
  providerConfig,
}: Props) {
  const rules = providerConfig.general_mapping ?? [];
  const save = useGeneralMappingSave();
  // eslint-disable-next-line security/detect-object-injection -- provider is a fixed union
  const providerLabel = PROVIDER_LABELS[provider] ?? provider;
  const externalOptions = useExternalPriorityOptions(
    family,
    tenantId,
    credentialId,
  );

  const hasField = (r: GeneralMappingRule) =>
    Boolean(r.priority_mapping && Object.keys(r.priority_mapping).length);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Priority translation</CardTitle>
        <CardDescription>
          Map each Harmony priority to a {providerLabel} priority. Harmony sets
          this priority on tickets it creates/updates in {providerLabel}.
          {family === "jira" && (
            <> The same mapping is used in reverse when importing from Jira.</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScopeReveal
          family={family}
          tenantId={tenantId}
          credentialId={credentialId}
          rules={rules}
          hasField={hasField}
          onClearScope={(match) => { save(match, { priority_mapping: null }); }}
          renderEditor={(match) => (
            <PriorityMappingEditor
              value={findRule(rules, match)?.priority_mapping ?? {}}
              onChange={(m) => {
                save(match, {
                  priority_mapping: Object.keys(m).length ? m : null,
                });
              }}
              harmonyOptions={HARMONY_PRIORITIES}
              harmonyLabels={HARMONY_PRIORITY_LABELS}
              providerLabel={providerLabel}
              externalOptions={externalOptions}
              bidirectional={family === "jira"}
            />
          )}
        />
      </CardContent>
    </Card>
  );
}
